import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import * as objectUtils from '~/common/utils/objectUtils';
import { IdProvider, unwrap } from '../common/apiCommons';
import axios from 'axios';
import _ from 'lodash';

enum JobTextRecordKeys {
  JOB_TEXT_RECORD = 'JOB_TEXT_RECORD',
  COUNT = 'COUNT',
}

interface Job {
  id: number;
  name: string;
  department: number;
  project: number;
  description: string;
}

export interface JobTextRecord {
  id: number;
  job: Job;
  employee: number;
  start: number;
  end: number;
  duration: number;
  comment: string;
  improvement_comment: string;
  took_too_long_comment: string;
  time_suggestion: number;
}

const queries = {
  async getTextRecordCount() {
    const res = await axios.get<{ count: number }>('/record/job/text/count/');
    return unwrap(res);
  },
  async getTextRecords(
    page: number,
    filterState: { employee?: number; project?: number; nullEmployee?: boolean },
  ): Promise<JobTextRecord[]> {
    let queryParams = `?page=${page}`;
    if (filterState && filterState.employee) {
      queryParams += `&employee=${filterState.employee}`;
    }
    if (filterState && filterState.project) {
      queryParams += `&project=${filterState.project}`;
    }
    if (filterState && filterState.nullEmployee) {
      queryParams += `&null_employee=true`;
    }
    const res = await axios.get<JobTextRecord[]>(
      `/record/job/text/${queryParams}`,
    );
    const records = unwrap(res);
    return records.map((record) =>
      _.set(
        record,
        'job.project',
        // Another dirty hack, because we we change stuff that comes from the api.
        _.first((record.job as unknown as { projects: number[] }).projects),
      ),
    );
  },
};

const mutations = {
  bulkDeleteJobTextRecords(
    page: number,
    filterState: { employee: number | undefined; project: number | undefined },
  ) {
    return axios
      .post<void>('/record/job/text/bulk/seen/', {
        filterState,
        page,
      })
      .then(unwrap);
  },
  async setTextRecordSeen(textRecord: IdProvider) {
    if (!textRecord?.id) {
      return null;
    }
    const res = await axios.patch<JobTextRecord>(
      `/record/job/text/${textRecord.id}/seen/`,
    );
    return unwrap(res);
  },
  async setDescriptionForJob(request: IdProvider & { description: string }) {
    if (!request?.id) {
      return null;
    }
    const { id, description } = request;
    const res = await axios.post<JobTextRecord>(
      `/record/job/text/${id}/text/`,
      {
        description,
      },
    );
    return unwrap(res);
  },
};

const hooks = {
  useTextRecordCount(options?: UseQueryOptions<{ count: number }>) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [JobTextRecordKeys.COUNT],
      queryFn: queries.getTextRecordCount,
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([JobTextRecordKeys.COUNT]),
    ];
    return QueryResultAndInvalidate;
  },
  useTextRecords(
    page: number,
    filterState: { employee?: number; routine?: number; nullEmployee?: boolean },
    options?: UseQueryOptions<JobTextRecord[]>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [JobTextRecordKeys.JOB_TEXT_RECORD, page, filterState],
      queryFn: () => queries.getTextRecords(page, filterState),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([JobTextRecordKeys.JOB_TEXT_RECORD]),
    ];
    return QueryResultAndInvalidate;
  },
  useTextRecordSeen() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.setTextRecordSeen,
      async onSuccess(_data, textRecord) {
        if (!textRecord?.id) {
          return;
        }
        const { id } = textRecord;
        const records: JobTextRecord[] =
          queryClient.getQueryData([JobTextRecordKeys.JOB_TEXT_RECORD]) ?? [];
        queryClient.setQueryData(
          [JobTextRecordKeys.JOB_TEXT_RECORD],
          _.filter(records, objectUtils.equal('id', id)) ?? [],
        );
        await queryClient.invalidateQueries([JobTextRecordKeys.COUNT]);
      },
    });
  },
  useBulkDeleteJobTextRecord(page: number) {
    const queryClient = useQueryClient();
    return useMutation(
      (filterState: {
        employee: number | undefined;
        project: number | undefined;
      }) => mutations.bulkDeleteJobTextRecords(page, filterState),
      {
        async onSuccess() {
          await queryClient.invalidateQueries(['RoutineRecord']);
        },
      },
    );
  },
  useSetJobDescription() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.setDescriptionForJob,
      onMutate({ id, description }) {
        const records: JobTextRecord[] =
          queryClient.getQueryData([JobTextRecordKeys.JOB_TEXT_RECORD]) ?? [];
        queryClient.setQueryData(
          [JobTextRecordKeys.JOB_TEXT_RECORD],
          _.map(records, (record) => {
            if (record.id !== id) {
              return record;
            }
            return _.set(record, 'job.description', description);
          }) ?? [],
        );

        return records;
      },
      onError(_err, _vars, prev) {
        queryClient.setQueryData([JobTextRecordKeys.JOB_TEXT_RECORD], prev);
      },
      async onSuccess() {
        await queryClient.invalidateQueries([JobTextRecordKeys.COUNT]);
      },
    });
  },
};

const JobTextRecordApi = {
  queries,
  mutations,
  hooks,
};

export default JobTextRecordApi;
